import { Calendar } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'
import { formatDate, parseDisplayDate } from '@/utils/date'

interface DateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max'> {
  /** yyyy-MM-dd, or empty */
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
}

/** A typed DD/MM/YYYY box with a calendar button; the value it reports is always yyyy-MM-dd. */
export function DateInput({ value, onChange, min, max, className, onBlur, ...props }: DateInputProps) {
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const [displayValue, setDisplayValue] = useState(formatDate(value))
  const [isInvalid, setIsInvalid] = useState(false)

  useEffect(() => {
    setDisplayValue(formatDate(value))
    setIsInvalid(false)
  }, [value])

  const handleTextChange = useCallback((next: string) => {
    const sanitized = next.replace(/[^\d/-]/g, '').slice(0, 10)
    setDisplayValue(sanitized)
    setIsInvalid(false)
    if (sanitized === '') {
      onChange('')
    } else if (sanitized.length === 10) {
      const parsed = parseDisplayDate(sanitized)
      if (parsed) onChange(parsed)
      else setIsInvalid(true)
    }
  }, [onChange])

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const parsed = parseDisplayDate(displayValue)
    if (parsed) onChange(parsed)
    // A half-typed date goes back to the last good one; an emptied box stays empty
    setDisplayValue(displayValue.trim() === '' ? '' : formatDate(parsed ?? value))
    setIsInvalid(false)
    onBlur?.(e)
  }, [displayValue, onChange, onBlur, value])

  const openNativePicker = useCallback(() => {
    const input = nativeInputRef.current
    if (!input) return
    if (typeof input.showPicker === 'function') input.showPicker()
    else input.click()
  }, [])

  return (
    <div className="relative min-w-0 flex-1">
      <Input
        {...props}
        type="text"
        inputMode="numeric"
        value={displayValue}
        placeholder="DD/MM/YYYY"
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleBlur}
        aria-invalid={isInvalid || props['aria-invalid']}
        className={cn('h-9 pr-9', isInvalid && 'border-destructive-400 focus-visible:ring-destructive-400', className)}
      />
      <button
        type="button"
        onClick={openNativePicker}
        disabled={props.disabled}
        className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-50"
        aria-label={props['aria-label'] ? `Choose ${props['aria-label']}` : 'Choose date'}
      >
        <Calendar className="h-4 w-4" />
      </button>
      <input
        ref={nativeInputRef}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-px w-px opacity-0"
      />
    </div>
  )
}
